import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import SignaturePad from 'signature_pad';

import Modal from 'common/components/Modal';
import { scaleStrokes } from 'lib/pdfs/autosur';

/**
 * Signing modal of every custody voucher (per asset and per employee). It knows
 * nothing about formats: the caller gives the preview and two callbacks.
 *  - onPaper(withPrice): "Descargar para firma en papel", the PDF without signature.
 *  - onSign(signatureImage, withPrice): builds the signed PDF and saves it; when
 *    it resolves the modal reports success, calls onSaved and closes.
 * Signature pad: signature_pad 5 (pointer events: mouse, touch and pen), sharp
 * on high-density screens, the strokes survive turning the phone.
 */
class SignVoucherModal extends PureComponent {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    preview: PropTypes.node,
    priceOption: PropTypes.bool,
    canSign: PropTypes.bool,
    onPaper: PropTypes.func.isRequired,
    onSign: PropTypes.func,
    onSaved: PropTypes.func,
    successMessage: PropTypes.string,
    error: PropTypes.func,
    success: PropTypes.func,
    t: PropTypes.func.isRequired,
  };

  static defaultProps = {
    priceOption: false,
    canSign: true,
    successMessage: 'success.custody_voucher_saved',
  };

  state = { empty: true, saving: false, withPrice: false };

  canvas = null;
  pad = null;

  componentWillUnmount = () => this.destroyPad();

  componentDidUpdate = (prevProps) => {
    if (prevProps.show && !this.props.show) this.destroyPad();
  };

  // Called on 'shown.bs.modal': before that the canvas measures 0.
  handleShown = () => {
    if (!this.canvas || this.pad) return;
    this.pad = new SignaturePad(this.canvas, { minWidth: 0.7, maxWidth: 2.2 });
    this.pad.addEventListener('endStroke', this.handleEndStroke);
    this.resizeCanvas();
    window.addEventListener('resize', this.resizeCanvas);
    window.addEventListener('orientationchange', this.resizeCanvas);
  };

  destroyPad = () => {
    window.removeEventListener('resize', this.resizeCanvas);
    window.removeEventListener('orientationchange', this.resizeCanvas);
    if (this.pad) {
      this.pad.removeEventListener('endStroke', this.handleEndStroke);
      this.pad.off();
      this.pad = null;
    }
    this.setState({ empty: true, saving: false, withPrice: false });
  };

  handleEndStroke = () => this.setState({ empty: this.pad.isEmpty() });

  // Sharp lines on high-density screens, and the signature survives turning
  // the phone: the strokes are scaled to the new size instead of cleared.
  resizeCanvas = () => {
    const canvas = this.canvas;
    if (!canvas || !this.pad || !canvas.offsetWidth) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const oldWidth = canvas.width / ratio;
    const oldHeight = canvas.height / ratio;
    const strokes = this.pad.toData();
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
    this.pad.clear();
    if (strokes.length && oldWidth && oldHeight) {
      this.pad.fromData(
        scaleStrokes(strokes, canvas.offsetWidth / oldWidth, canvas.offsetHeight / oldHeight)
      );
    }
  };

  handleClear = (e) => {
    e.preventDefault();
    if (this.pad) this.pad.clear();
    this.setState({ empty: true });
  };

  handlePrice = (withPrice) => () => this.setState({ withPrice });

  handlePaper = async (e) => {
    e.preventDefault();
    try {
      await this.props.onPaper(this.state.withPrice);
    } catch (err) {
      console.error('Error generating the custody voucher', err);
      if (this.props.error) this.props.error('error.generate_pdf');
    }
  };

  handleSign = async (e) => {
    e.preventDefault();
    if (!this.pad || this.pad.isEmpty() || this.state.saving || !this.props.onSign) return;
    this.setState({ saving: true });
    try {
      await this.props.onSign(this.pad.toDataURL('image/png'), this.state.withPrice);
      if (this.props.success) this.props.success(this.props.successMessage);
      if (this.props.onSaved) this.props.onSaved();
      this.props.handleClose();
    } catch (err) {
      console.error('Error saving the signed custody voucher', err);
      if (this.props.error) this.props.error((err && err.message) || 'error.server_error');
      this.setState({ saving: false });
    }
  };

  renderPriceOption = () => {
    const { t } = this.props;
    const { withPrice, saving } = this.state;
    return (
      <div className="btn-group btn-group-sm mb-3" role="group">
        <button
          type="button"
          className={`btn ${withPrice ? 'btn-outline-secondary' : 'btn-secondary'}`}
          onClick={this.handlePrice(false)}
          disabled={saving}
          aria-pressed={!withPrice}
        >
          {t('custodyVoucher.withoutPrice')}
        </button>
        <button
          type="button"
          className={`btn ${withPrice ? 'btn-secondary' : 'btn-outline-secondary'}`}
          onClick={this.handlePrice(true)}
          disabled={saving}
          aria-pressed={withPrice}
        >
          {t('custodyVoucher.withPrice')}
        </button>
      </div>
    );
  };

  render = () => {
    const { t, show, title, preview, priceOption, canSign } = this.props;
    const { empty, saving } = this.state;
    return (
      <Modal
        show={show}
        handleClose={this.props.handleClose}
        onShown={this.handleShown}
        size="lg"
        scrollable={true}
      >
        <div>
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button
              type="button"
              className="close"
              onClick={this.props.handleClose}
              disabled={saving}
              aria-label={t('actions.cancel')}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {priceOption && this.renderPriceOption()}
            {show && preview}
            {canSign && (
              <div>
                <label className="font-weight-bold mb-1">{t('custodyVoucher.signature')}</label>
                <div className="text-muted small mb-1">{t('custodyVoucher.signatureHelp')}</div>
                <canvas
                  ref={(element) => (this.canvas = element)}
                  className="border rounded d-block w-100"
                  style={{ height: '180px', background: '#fff', touchAction: 'none' }}
                />
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0"
                  onClick={this.handleClear}
                  disabled={saving}
                >
                  {t('custodyVoucher.clear')}
                </button>
              </div>
            )}
          </div>
          <div className="modal-footer flex-wrap">
            <button
              type="button"
              className="btn btn-outline-secondary mr-auto"
              onClick={this.handlePaper}
              disabled={saving}
            >
              <i className="fa fa-file-pdf-o mr-1" /> {t('custodyVoucher.downloadForPaper')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={this.props.handleClose}
              disabled={saving}
            >
              {t('actions.cancel')}
            </button>
            {canSign && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={this.handleSign}
                disabled={empty || saving}
              >
                {saving ? <i className="fa fa-spinner fa-spin mr-1" /> : null}
                {t('custodyVoucher.signAndSave')}
              </button>
            )}
          </div>
        </div>
      </Modal>
    );
  };
}

export default translate()(SignVoucherModal);



// WEBPACK FOOTER //
// src/modules/common/components/SignVoucherModal.js